import React, {useMemo} from "react";
import {ArrowLongRightIcon, MapPinIcon, TruckIcon, UserIcon} from "@heroicons/react/24/outline";
import {TrailerIcon} from "../../../../data/themes/icons";
import {numberWithCommas} from "../../../util/util-formaters";
import {checkPerm, openInNewTab} from "../../../util/util-helpers";
import Resources from "../../../../data/services/resources";
import {READ_PERM, UPDATE_PERM} from "../../../util/util-consts";

export default function LoadDispatchItems({switches, translate}) {

    const parsedSwitches = useMemo(() => {
        if (!switches) return [];
        if (typeof switches === 'string') {
            try {
                return JSON.parse(switches);
            } catch (e) {
                return [];
            }
        }
        return switches;
    }, [switches]);

    const canViewDriver = checkPerm(Resources.DriverInfo, UPDATE_PERM);
    const canViewTruck = checkPerm(Resources.TrucksLocations, READ_PERM);
    const canViewTrailer = checkPerm(Resources.TrailersLocations, READ_PERM);

    // Calculate total mileage per driver
    const driverMileageSummary = useMemo(() => {

        const mileageByDriver = {};

        parsedSwitches.forEach((leg) => {
            const driverId = leg.DriverID;
            const driverName = leg.Driver || translate("text.unknown_driver");

            if (!mileageByDriver[driverId]) {
                mileageByDriver[driverId] = {
                    driverId,
                    driverName,
                    totalMileage: 0
                };
            }
            mileageByDriver[driverId].totalMileage += leg.Mileage || 0;
        });

        return Object.values(mileageByDriver);
    }, [parsedSwitches, translate]);

    // Get leg number (excluding empty leg from count)
    const getLegNumber = (index) => {
        let legNum = 0;
        for (let i = 0; i <= index; i++) {
            if (!parsedSwitches[i].Empty) {
                legNum++;
            }
        }
        return legNum;
    };

    function AssetLink({id, name, canView, path, placeholder = "-"}) {
        if (!name) return <span>{placeholder}</span>;
        if (canView && id) {
            return (
                <button
                    className="text-primary hover:underline truncate text-left"
                    onClick={() => openInNewTab(path)}
                >
                    {name}
                </button>
            );
        }
        return <span className="truncate">{name}</span>;
    }

    return (
        <div className="p-6">
            <ul className="relative">
                {parsedSwitches.map((leg, index) => (
                    <li key={index} className="relative pb-6 last:pb-0">
                        {/* Timeline connector line */}
                        {index < parsedSwitches.length - 1 && (
                            <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-tm-gray-200"/>
                        )}

                        <div className="flex gap-4">
                            {/* Timeline dot */}
                            <div className="relative flex-shrink-0">
                                <div
                                    className="w-8 h-8 rounded-full border-2 border-tm-gray-300 bg-inverse flex items-center justify-center">
                                    {leg.Empty ? (
                                        <span className="text-xs font-bold text-primary pt-px">E</span>
                                    ) : (
                                        <span className="text-xs font-medium text-primary font-bold">
                                            {getLegNumber(index)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Leg content */}
                            <div className="flex-1 bg-tm-gray-50 rounded-lg p-4 border border-tm-gray-200">
                                {/* Header with leg type and mileage */}
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        {leg.Empty ? (
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-primary-transparent text-primary border border-primary">
                                                {translate("text.deadhead")}
                                            </span>
                                        ) : (
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-primary text-primary-contrast">
                                                {translate("text.leg")} {getLegNumber(index)}
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-sm font-bold text-tm-gray-600">
                                        {leg.Mileage} {translate("text.miles")}
                                    </span>
                                </div>

                                {/* From -> To */}
                                <div className="flex items-center gap-2 mb-3 text-sm">
                                    <MapPinIcon className="size-5 text-tm-gray-600 font-bold flex-shrink-0"/>
                                    <span className="text-tm-gray-700 truncate"
                                          title={leg.From || translate("text.unknown")}>
                                        {leg.From || translate("text.unknown")}
                                    </span>
                                    <ArrowLongRightIcon className="w-5 h-5 text-tm-gray-700 font-bold flex-shrink-0"/>
                                    <span className="text-tm-gray-700 truncate" title={leg.To || translate("text.unknown")}>
                                        {leg.To || translate("text.unknown")}
                                    </span>
                                </div>

                                {/* Assets grid */}
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                    {/* Driver */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        <UserIcon className="size-5 text-tm-gray-600 font-bold flex-shrink-0"/>
                                        <span className="text-sm/6 font-bold text-tm-gray-900 shrink-0">{translate("text.driver")}:</span>
                                        <AssetLink
                                            id={leg.DriverID}
                                            name={leg.Driver}
                                            canView={canViewDriver}
                                            path={`/drivers/info/${leg.DriverID}`}
                                        />
                                    </div>

                                    {/* Co-Driver */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        <UserIcon className="size-5 text-tm-gray-600 font-bold flex-shrink-0"/>
                                        <span className="text-sm/6 font-bold text-tm-gray-900 shrink-0">{translate("text.co_driver")}:</span>
                                        <AssetLink
                                            id={leg.CoDriverID}
                                            name={leg.CoDriver}
                                            canView={canViewDriver}
                                            path={`/drivers/info/${leg.CoDriverID}`}
                                        />
                                    </div>

                                    {/* Truck */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        <TruckIcon className="size-5 text-tm-gray-600 font-bold flex-shrink-0"/>
                                        <span className="text-sm/6 font-bold text-tm-gray-900 shrink-0">{translate("text.truck")}:</span>
                                        <AssetLink
                                            id={leg.TruckID}
                                            name={leg.Truck}
                                            canView={canViewTruck}
                                            path={`/trucks/info/${leg.TruckID}?tab=trucks/locations`}
                                        />
                                    </div>

                                    {/* Trailer */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        <TrailerIcon className="size-5 text-tm-gray-600 flex-shrink-0" strokeWidth={1.5}/>
                                        <span className="text-sm/6 font-bold text-tm-gray-900 shrink-0">{translate("text.trailer")}:</span>
                                        <AssetLink
                                            id={leg.TrailerID}
                                            name={leg.Trailer}
                                            canView={canViewTrailer}
                                            path={`/trailers/info/${leg.TrailerID}?tab=trailers/locations`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            {/* Driver mileage summary */}
            <div className="mt-6 pt-4 border-t border-tm-gray-200">
                <h4 className="text-sm font-semibold text-tm-gray-700 mb-3">
                    {translate("text.mileage_by_driver")}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                    {driverMileageSummary.map((summary) => (
                        <div
                            key={summary.driverId}
                            className="flex items-center justify-between bg-tm-gray-50 rounded-lg px-3 py-2 border border-tm-gray-200"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <UserIcon className="size-5 text-tm-gray-600 font-bold flex-shrink-0"/>
                                {canViewDriver && summary.driverId ? (
                                    <button
                                        className="text-sm text-primary hover:underline font-medium truncate text-left"
                                        onClick={() => openInNewTab(`/drivers/info/${summary.driverId}`)}
                                    >
                                        {summary.driverName}
                                    </button>
                                ) : (
                                    <span className="text-sm text-tm-gray-700 font-medium truncate">
                                        {summary.driverName}
                                    </span>
                                )}
                            </div>
                            <span className="text-sm font-bold text-tm-gray-700 shrink-0 ml-2">
                                {numberWithCommas(summary.totalMileage)} {translate("text.mi")}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
